import { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import styles from './AuthForm.module.scss';

import { Button } from '@/components/new-order/common/Button';
import BackButton from '@/components/new-order/common/BackButton';
import CaptchaDisclaimer from '@/components/affiliate/AuthScreen/CaptchaDisclaimer';

import { constructSniperLink } from '@/utils/emailSniperLinks';
import { sendOneTimeCodeVerifyRequest } from '@/utils/api/login';
import { isApiError } from '@/utils/api/clearApiClient';
import { httpResponseCodes } from '@/utils/api/codeConsts';
import InputError from '@/components/new-order/common/InputError';
import { useAffiliateDispatch } from '@/store/affiliate';
import { fetchProfile } from '@/store/new-order/userSlice';
import analyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';

const initialCountdownTimeSeconds = 59;
const intervalMills = 1000;
const secondsStringLength = 2;

const codeLength = 6;

let interval: NodeJS.Timeout;
const launchCountDownTimer = (
  setCountdownResetTimer: React.Dispatch<React.SetStateAction<number>>,
  setIsResetButtonEnabled: (enabled: boolean) => void,
) => {
  interval = setInterval(() => {
    setCountdownResetTimer((value) => {
      if (value <= 1) {
        clearInterval(interval);
        setIsResetButtonEnabled(true);

        return 0;
      }

      return value - 1;
    });
  }, intervalMills);
};

interface OneTimeCodeFormProps {
  email: string;
  sendOTCRequest: (emailAdress: string) => Promise<void>;
  onBackButtonClick: () => void;
  onAuthSuccess: () => void;
}

const OneTimeCodeForm = ({ email, sendOTCRequest, onBackButtonClick, onAuthSuccess }: OneTimeCodeFormProps) => {
  const dispatch = useAffiliateDispatch();
  const intl = useIntl();

  const codeInputRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const sniperLink = constructSniperLink(email);

  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [countdownResetTimer, setCountdownResetTimer] = useState(initialCountdownTimeSeconds);
  const [isResetButtonEnabled, setIsResetButtonEnabled] = useState(false);
  const [isResendButtonLoading, setIsResendButtonLoading] = useState(false);

  useEffect(() => {
    launchCountDownTimer(setCountdownResetTimer, setIsResetButtonEnabled);
    codeInputRef.current?.focus();
  }, []);

  const onCodeChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.value.length === codeLength) {
      submitButtonRef.current?.focus();
    }
  };

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();

    const code = codeInputRef.current?.value;
    const isValid = code && codeInputRef.current?.validity.valid;

    if (isValid) {
      setIsSubmitting(true);
      setError('');
      sendOneTimeCodeVerifyRequest(email, code)
        .then(() => {
          analyticsEventHandler.logOTCCodeSubmitSuccess();

          return dispatch(fetchProfile()).then(onAuthSuccess);
        })
        .catch((err: unknown) => {
          console.log(err);
          if (isApiError(err) && err.response?.status === httpResponseCodes.BAD_REQUEST) {
            setError(intl.formatMessage({ id: 'authScreen.otcForm.errors.wrongCode', defaultMessage: 'Wrong code' }));
          } else {
            setError(
              intl.formatMessage({ id: 'authScreen.otcForm.errors.generic', defaultMessage: 'Something went wrong' }),
            );
          }
          setIsSubmitting(false);
        });
    }
  };

  const onResendEmailClick = () => {
    setIsResendButtonLoading(true);

    void sendOTCRequest(email).then(() => {
      setIsResetButtonEnabled(false);
      setCountdownResetTimer(initialCountdownTimeSeconds);
      launchCountDownTimer(setCountdownResetTimer, setIsResetButtonEnabled);

      analyticsEventHandler.logOTCEmailResend();

      setIsResendButtonLoading(false);
    });
  };

  return (
    <div className={styles.wrapper}>
      <form className={styles.form} onSubmit={onSubmit}>
        <BackButton onClick={onBackButtonClick} />
        <h3 className={styles.title}>
          <FormattedMessage id="authScreen.otcForm.title" defaultMessage="Enter the verification code" />
        </h3>
        <span className={styles.subtitle}>
          <FormattedMessage
            id="authScreen.otcForm.subtitle"
            defaultMessage="Check your inbox and sign in with the magic link or paste the activation 6-digit activation code below"
          />
          <br />
          <b>{email}</b>
          {sniperLink && (
            <a className={styles.subtitleButton} href={sniperLink}>
              <FormattedMessage
                id="authScreen.otcForm.sniperLink"
                defaultMessage="Open inbox"
                description="Link that will magically open user's email client with our email highlighted"
              />
            </a>
          )}
        </span>

        <div className={styles.otcWrapper}>
          <input
            type="text"
            autoComplete="one-time-code"
            inputMode="numeric"
            minLength={codeLength}
            maxLength={codeLength}
            pattern="\d{6}"
            className={styles.otcInput}
            disabled={isSubmitting}
            ref={codeInputRef}
            onChange={onCodeChange}
          />
        </div>
        {error && <InputError error={error} />}

        <Button type="submit" kind="primary" ref={submitButtonRef} loading={isSubmitting}>
          <FormattedMessage
            id="authScreen.otcForm.submitCode"
            defaultMessage="Apply the code"
            description="Button that submits the OTC code"
          />
        </Button>

        <div className={styles.resendEmailBlock}>
          {isResetButtonEnabled ? (
            <Button
              kind="tertiary"
              loading={isResendButtonLoading}
              className={styles.resendEmailButton}
              onClick={onResendEmailClick}
            >
              <FormattedMessage
                id="authScreen.otcForm.resendEmailButton"
                defaultMessage="Resend email"
                description="Button that resends OTC email"
              />
            </Button>
          ) : (
            <span className={styles.resendEmailCountdown}>
              <FormattedMessage
                id="authScreen.otcForm.resendEmailCountdown"
                defaultMessage="Resend email in"
                description="Countdown timer for the resend email button"
              />{' '}
              {countdownResetTimer > 0 && (
                <b className={styles.resendEmailTimer}>
                  0:{countdownResetTimer.toString().padStart(secondsStringLength, '0')}
                </b>
              )}
            </span>
          )}
        </div>

        <CaptchaDisclaimer />
      </form>
    </div>
  );
};

export default OneTimeCodeForm;
